<template>
  <div v-if="ready" class="layout">
    <!-- Top toolbar -->
    <div class="topbar">
      <button class="tool" :class="{ active: tool === 'pan' }" @click="setTool('pan')">🖐 Mano</button>
      <button class="tool" :class="{ active: tool === 'length' }" @click="setTool('length')">📏 Regla</button>
      <button class="tool" :class="{ active: tool === 'label' }" @click="setTool('label')">🏷️ Label</button>
      <button class="tool" :class="{ active: tool === 'ellipse' }" @click="setTool('ellipse')">⭕ Óvalo</button>
      <button class="tool" :class="{ active: tool === 'freehand' }" @click="setTool('freehand')">✏️ Libre</button>
      <button class="tool" :class="{ active: tool === 'rotate' }" @click="setTool('rotate')">⟳ Rotar</button>
      <span class="spacer"></span>
      <!-- Presets de rotación -->
      <div class="btn-group" style="display:flex; gap:6px">
        <button class="btn" @click="setRotation(0)">0°</button>
        <button class="btn" @click="setRotation(30)">30°</button>
        <button class="btn" @click="setRotation(45)">45°</button>
        <button class="btn" @click="setRotation(90)">90°</button>
        <button class="btn" @click="setRotation(180)">180°</button>
      </div>
      <button class="tool" title="Reset (R)" @click="resetView">↩️ Reset</button>
      <button class="tool" title="Guardar (S)" @click="saveToServer">💾 Guardar</button>
      <button class="tool" title="Descargar (D)" @click="downloadPng">⬇️ Descargar</button>
    </div>

    <!-- 2 paneles -->
    <div class="wrap">
      <!-- Panel izquierdo: viewport -->
      <div id="viewportWrap">
        <canvas ref="canvas"></canvas>
        <canvas ref="overlay" :style="{
          cursor: wlwwAdjusting
            ? 'ns-resize'
            : (tool === 'pan' ? (isDragging ? 'grabbing' : 'grab') : 'crosshair')
        }" @contextmenu.prevent></canvas>
        <div class="hud">{{ hudText }}</div>
      </div>

      <!-- Panel derecho: controles -->
      <aside class="right">
        <div class="metric"><b>Paciente:</b> {{ meta.paciente.identificacion }} — {{ meta.paciente.nombre }} {{
          meta.paciente.apellido }}</div>
        <div class="metric"><b>Médico:</b> {{ meta.doctor.nombre }} {{ meta.doctor.apellido }}</div>
        <div class="metric"><b>Pixel Spacing:</b> {{ meta.pixel_spacing_x }} × {{ meta.pixel_spacing_y }} mm/px</div>
        <div class="metric"><b>Slices:</b> {{ meta.total_slices }}</div>

        <div class="group">
          <div class="label">Brillo (L)</div>
          <input class="slider" type="range" min="-255" max="255" step="1" v-model.number="viewport.wl" @input="draw">
        </div>
        <div class="group">
          <div class="label">Contraste (W)</div>
          <input class="slider" type="range" min="1" max="512" step="1" v-model.number="viewport.ww" @input="draw">
        </div>
        <div class="group">
          <div class="label">Zoom</div>
          <input class="slider" type="range" min="25" max="400" step="1" :value="Math.round(viewport.zoom * 100)"
            @input="onZoomSlider">
          <div class="row btns">
            <button class="btn" @click="fitContain">Ajustar</button>
            <button class="btn" @click="fitWidth">Ancho</button>
            <button class="btn" @click="actualSize">1:1</button>
          </div>
        </div>
        <div class="hint">Atajos: R reset · S guardar · D descargar · M regla · L label · rueda = zoom</div>
      </aside>
    </div>
  </div>

  <div v-else style="padding:24px">Cargando visor…</div>
</template>

<script>
import axios from 'axios'
import rutas from '@/assets/utils/rutas.json'
import { fileService } from '@/shared/services'
import { imagenesService } from '@/views/imagen/services'

export default {
  name: 'Dicomviewer',
  data() {
    return {
      ready: false, meta: null,
      tool: 'pan',
      isDragging: false, lastX: 0, lastY: 0,
      drawing: null,                          // {type:'length'|'label', x0,y0,x1,y1, text?}
      viewport: { tool: 'pan', zoom: 1, translateX: 0, translateY: 0, wl: -40, ww: 200, rotateDeg: 0 },
      rotateDragSensitivity: 0.3,
      rotateStartDeg: 0,
      currentSlice: 0, ctx2d: null, octx2d: null, baseImage: null, imgW: 0, imgH: 0,
      annotations: { length: [], arrows: [], ellipses: [], freehands: [] },
      wlwwAdjusting: false,
      wlwwStart: { x: 0, y: 0, wl: 0, ww: 0 },
      wlwwSensitivity: { wlPerPx: 1.0, wwPerPx: 1.0 }, // ajusta a gusto
    }
  },
  computed: {
    hudText() {
      const deg = Math.round((this.viewport.rotateDeg % 360 + 360) % 360)
      return `Zoom: ${Math.round(this.viewport.zoom * 100)}% · WL: ${this.viewport.wl} · WW: ${this.viewport.ww} · Rot: ${deg}° · Slice ${this.currentSlice + 1}/${this.meta?.total_slices || 1}`
    }
  },
  async mounted() {
    const tokenParam = this.$route.params.token || this.$route.query.token
    if (!tokenParam) { alert('Falta token en la ruta'); this.$router.back(); return }

    const base = (rutas.axios || 'http://localhost:8000').replace(/\/$/, '')
    const metaUrl = `${base}/api/dicom/meta/${encodeURIComponent(tokenParam)}/`

    try {
      const { data } = await axios.get(metaUrl, { withCredentials: false })
      this.meta = data

      this.annotations = data.annotations || { length: [], arrows: [], ellipses: [], freehands: [] };
      // si guardaste con otros nombres, normalízalos aquí:
      this.annotations.length = this.annotations.length || [];
      this.annotations.arrows = this.annotations.arrows || [];
      this.annotations.ellipses = this.annotations.ellipses || [];
      this.annotations.freehands = this.annotations.freehands || [];

      this.currentSlice = data.initial_slice || 0

      this.ready = true
      await this.$nextTick()

      this.ctx2d = this.$refs.canvas.getContext('2d')
      this.octx2d = this.$refs.overlay.getContext('2d')
      window.addEventListener('resize', this.onResize)

      await this.loadSlice(this.currentSlice)
      this.bindEvents()
    } catch (err) {
      console.error('Error meta', err?.response || err)
      alert('No se pudo cargar el estudio.')
      this.$router.back()
    }
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.onResize)
  },
  methods: {
    /* ===== UI ===== */
    setTool(name) {
      this.tool = name
    },
    clampWW(v) { return Math.max(1, Math.min(512, v)) },
    clampWL(v) { return Math.max(-255, Math.min(255, v)) },

    resetView() {
      this.annotations.length = []
      this.annotations.arrows = []
      this.annotations.ellipses = []
      this.annotations.freehands = []
      this.viewport.wl = -40; this.viewport.ww = 200
      this.viewport.rotateDeg = 0
      this.fit('contain')
    },
    drawEllipse(x0, y0, x1, y1) {
      const ctx = this.octx2d
      const cx = (x0 + x1) / 2, cy = (y0 + y1) / 2
      const rx = Math.abs(x1 - x0) / 2, ry = Math.abs(y1 - y0) / 2
      ctx.beginPath()
      ctx.ellipse(cx, cy, Math.max(1e-3, rx), Math.max(1e-3, ry), 0, 0, Math.PI * 2)
      ctx.stroke()
    },
    setRotation(deg) {
      this.viewport.rotateDeg = this.normalizeDeg(deg)
      this.draw()
    },
    addRotation(delta) {
      this.setRotation(this.viewport.rotateDeg + delta)
    },
    normalizeDeg(d) {
      d = d % 360
      return d < 0 ? d + 360 : d
    },

    drawFreehand(points) {
      const ctx = this.octx2d
      if (!points || points.length < 2) return
      ctx.save()
      ctx.lineJoin = 'round'
      ctx.lineCap = 'round'
      ctx.beginPath()
      ctx.moveTo(points[0].x, points[0].y)
      for (let i = 1; i < points.length; i++) ctx.lineTo(points[i].x, points[i].y)
      ctx.stroke()
      ctx.restore()
    },

    /* ===== Carga y render ===== */
    imageUrlForSlice(i) {
      const u = this.meta.preview_url
      return (this.meta.total_slices > 1) ? `${u}?slice=${i}` : u
    },
    async loadSlice(i) {
      const img = new Image()
      img.crossOrigin = 'anonymous'
      await new Promise((ok, err) => { img.onload = ok; img.onerror = err; img.src = this.imageUrlForSlice(i) })
      this.baseImage = img
      this.imgW = img.naturalWidth; this.imgH = img.naturalHeight

      const wrap = this.$refs.canvas.parentElement
      this.$refs.canvas.width = this.$refs.overlay.width = wrap.clientWidth
      this.$refs.canvas.height = this.$refs.overlay.height = wrap.clientHeight

      this.fit('contain')
      this.draw()
    },
    applyWL() {
      const contrast = Math.max(0.05, this.viewport.ww / 255)
      const brightness = this.viewport.wl / 255
      this.ctx2d.filter = `brightness(${1 + brightness}) contrast(${contrast})`
    },
    applyViewportTransform(ctx) {
      const z = this.viewport.zoom
      const tx = this.viewport.translateX
      const ty = this.viewport.translateY
      const theta = this.viewport.rotateDeg * Math.PI / 180
      const cx = this.imgW / 2, cy = this.imgH / 2

      ctx.setTransform(1, 0, 0, 1, 0, 0)          // identidad
      ctx.translate(tx, ty)                   // mover imagen en pantalla
      ctx.scale(z, z)                         // escalar en unidades de imagen
      ctx.translate(cx, cy)                   // origen al centro de la imagen
      ctx.rotate(theta)                       // rotar alrededor del centro
      ctx.translate(-cx, -cy)                 // volver el origen a (0,0) de imagen
    },
    draw() {
      const c = this.$refs.canvas, o = this.$refs.overlay
      this.ctx2d.setTransform(1, 0, 0, 1, 0, 0); this.ctx2d.clearRect(0, 0, c.width, c.height)
      this.octx2d.setTransform(1, 0, 0, 1, 0, 0); this.octx2d.clearRect(0, 0, o.width, o.height)

      this.ctx2d.imageSmoothingEnabled = false
      this.octx2d.imageSmoothingEnabled = false

      this.applyViewportTransform(this.ctx2d)
      this.applyWL()
      this.ctx2d.drawImage(this.baseImage, 0, 0)
      this.ctx2d.filter = 'none'

      this.applyViewportTransform(this.octx2d)


      this.octx2d.strokeStyle = 'red'; this.octx2d.fillStyle = 'red'
      this.octx2d.lineWidth = 1 / Math.max(0.5, this.viewport.zoom)

      for (const a of this.annotations.length) this.drawLength(a.x0, a.y0, a.x1, a.y1)
      for (const a of this.annotations.arrows) this.drawArrow(a.x0, a.y0, a.x1, a.y1, a.text)
      for (const e of this.annotations.ellipses) this.drawEllipse(e.x0, e.y0, e.x1, e.y1)
      for (const f of this.annotations.freehands) this.drawFreehand(f.points)
      if (this.drawing) {
        if (this.drawing.type === 'length') this.drawLength(this.drawing.x0, this.drawing.y0, this.drawing.x1, this.drawing.y1)
        if (this.drawing.type === 'label') this.drawArrow(this.drawing.x0, this.drawing.y0, this.drawing.x1, this.drawing.y1, this.drawing.text || '')
        if (this.drawing.type === 'ellipse') this.drawEllipse(this.drawing.x0, this.drawing.y0, this.drawing.x1, this.drawing.y1)
        if (this.drawing.type === 'freehand') this.drawFreehand(this.drawing.points)
      }
    },

    drawArrow(x0, y0, x1, y1, text) {
      const ctx = this.octx2d

      ctx.beginPath()
      ctx.moveTo(x0, y0)
      ctx.lineTo(x1, y1)
      ctx.stroke()

      const ang = Math.atan2(y1 - y0, x1 - x0)
      const sz = 8 / this.viewport.zoom
      ctx.beginPath()
      ctx.moveTo(x1, y1)
      ctx.lineTo(x1 - sz * Math.cos(ang - Math.PI / 6), y1 - sz * Math.sin(ang - Math.PI / 6))
      ctx.lineTo(x1 - sz * Math.cos(ang + Math.PI / 6), y1 - sz * Math.sin(ang + Math.PI / 6))
      ctx.closePath()
      ctx.fill()

      if (!text) return

      const scr = this.toScreen(x1, y1)
      ctx.save()
      ctx.setTransform(1, 0, 0, 1, 0, 0)

      // fijar fuente ANTES de medir
      ctx.font = '12px system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, sans-serif'
      const w = ctx.measureText(text).width + 12

      ctx.fillStyle = 'rgba(0,0,0,.6)'
      ctx.fillRect(scr.x + 8, scr.y - 18, w, 20)

      ctx.fillStyle = '#fff'
      ctx.fillText(text, scr.x + 12, scr.y - 4)
      ctx.restore()
    },


    drawLength(x0, y0, x1, y1) {
      const ctx = this.octx2d, pxX = this.meta.pixel_spacing_x, pxY = this.meta.pixel_spacing_y
      ctx.beginPath(); ctx.moveTo(x0, y0); ctx.lineTo(x1, y1); ctx.stroke()
      ctx.beginPath(); ctx.arc(x0, y0, 3 / this.viewport.zoom, 0, Math.PI * 2); ctx.fill()
      ctx.beginPath(); ctx.arc(x1, y1, 3 / this.viewport.zoom, 0, Math.PI * 2); ctx.fill()

      const dx = (x1 - x0) * pxX, dy = (y1 - y0) * pxY, mm = Math.sqrt(dx * dx + dy * dy)
      const label = `${mm.toFixed(2)} mm`, mid = { x: (x0 + x1) / 2, y: (y0 + y1) / 2 }
      const scr = this.toScreen(mid.x, mid.y)
      ctx.save(); ctx.setTransform(1, 0, 0, 1, 0, 0)
      ctx.font='12px system-ui'
      ctx.fillStyle='rgba(0,0,0,.6)'
      const w = ctx.measureText(label).width + 10
      ctx.fillRect(scr.x+6, scr.y-16, w, 18)
      ctx.fillStyle='#fff'
      ctx.fillText(label, scr.x + 10, scr.y - 4)
      ctx.restore()
    },

    /* ===== mezclar/exportar/guardar ===== */
    async mergedCanvasBlob() {
      const out = document.createElement('canvas')
      out.width = this.$refs.canvas.width; out.height = this.$refs.canvas.height
      const t = out.getContext('2d')
      t.drawImage(this.$refs.canvas, 0, 0)
      t.drawImage(this.$refs.overlay, 0, 0)
      return await new Promise(res => out.toBlob(res, 'image/png', 0.95))
    },
    toRelativePath(urlOrPath) {
      if (!urlOrPath) return null
      try { return new URL(urlOrPath, window.location.origin).pathname } catch { return urlOrPath }
    },
    async uploadDicomPNG(blob) {
      const filename = `dicom_${this.$route.params.token}_slice${this.currentSlice + 1}.png`
      const file = new File([blob], filename, { type: 'image/png' })
      const deleteFile = this.toRelativePath(this.meta?.url_dicom_modificado || null)
      return await fileService.upload('dicom_mod', file, 'doctor', deleteFile)
    },
    async saveToServer() {
      try {
        const idEnvioFromLS = localStorage.getItem("envio_imagen_id")
        const idInformeLS = localStorage.getItem("idInforme")

        const blob = await this.mergedCanvasBlob()
        const urlDicomMod = await this.uploadDicomPNG(blob)
        if (!urlDicomMod) throw new Error('upload falló')

        const payload = {
          idInforme: idInformeLS || null,
          id_envio_imagen: idEnvioFromLS || null,
          url_dicom_modificado: urlDicomMod,
          dicomAnnotations: this.annotations,
        }
        const resp = await imagenesService.actualizarInforme(payload)
        if (resp?.status !== 'Success') throw new Error('GraphQL no confirmó éxito')

        this.meta.url_dicom_modificado = urlDicomMod
        await this.$swal.fire({ icon: 'success', title: 'Guardado', text: 'Imagen modificada almacenada.' })
      } catch (e) {
        console.error(e)
        this.$swal.fire({ icon: 'error', title: 'Error', text: 'No se pudo guardar.' })
      }
    },
    async downloadPng() {
      const blob = await this.mergedCanvasBlob()
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url; a.download = `dicom_${this.$route.params.token}_slice${this.currentSlice + 1}.png`
      a.click(); URL.revokeObjectURL(url)
    },

    /* ===== zoom/movimiento ===== */
    fit(mode = 'contain') {
      const cw = this.$refs.canvas.width, ch = this.$refs.canvas.height
      const sx = cw / this.imgW, sy = ch / this.imgH
      if (mode === 'width') this.viewport.zoom = sx
      else if (mode === 'height') this.viewport.zoom = sy
      else if (mode === 'cover') this.viewport.zoom = Math.max(sx, sy)
      else this.viewport.zoom = Math.min(sx, sy)
      this.viewport.translateX = (cw - this.imgW * this.viewport.zoom) / 2
      this.viewport.translateY = (ch - this.imgH * this.viewport.zoom) / 2
      this.draw()
    },
    fitWidth() { this.fit('width') },
    fitContain() { this.fit('contain') },
    actualSize() { this.viewport.zoom = 1; this.fit('contain') },

    onResize() { this.loadSlice(this.currentSlice) },
    toImg(sx, sy) {
      const { zoom: z, translateX: tx, translateY: ty, rotateDeg } = this.viewport
      const theta = rotateDeg * Math.PI / 180
      const cx = this.imgW / 2, cy = this.imgH / 2

      // deshacer translate y scale
      let x = (sx - tx) / z
      let y = (sy - ty) / z

      // llevar al centro
      x -= cx; y -= cy

      // rotación inversa (-theta)
      const cos = Math.cos(theta), sin = Math.sin(theta)
      const ix = x * cos + y * sin
      const iy = -x * sin + y * cos

      // volver del centro
      return { x: ix + cx, y: iy + cy }
    },

    toScreen(ix, iy) {
      const { zoom: z, translateX: tx, translateY: ty, rotateDeg } = this.viewport
      const theta = rotateDeg * Math.PI / 180
      const cx = this.imgW / 2, cy = this.imgH / 2

      // trasladar al centro
      let x = ix - cx, y = iy - cy

      // rotación directa (+theta)
      const cos = Math.cos(theta), sin = Math.sin(theta)
      const rx = x * cos - y * sin
      const ry = x * sin + y * cos

      // 🔧 volver del centro ( +cx,+cy ), luego escalar y por último trasladar
      const sx = (rx + cx) * z + tx
      const sy = (ry + cy) * z + ty
      return { x: sx, y: sy }
    },
    zoomAt(factor, sx, sy) {
      const before = this.toImg(sx, sy)
      this.viewport.zoom = Math.min(Math.max(this.viewport.zoom * factor, 0.25), 4.0)
      const after = this.toImg(sx, sy)
      this.viewport.translateX += (after.x - before.x) * this.viewport.zoom
      this.viewport.translateY += (after.y - before.y) * this.viewport.zoom
      this.draw()
    },
    onZoomSlider(e) {
      const target = e.target.value / 100
      const factor = target / this.viewport.zoom
      this.zoomAt(factor, this.$refs.canvas.width / 2, this.$refs.canvas.height / 2)
    },

    /* ===== eventos de dibujo ===== */
    bindEvents() {
      const ov = this.$refs.overlay
      ov.addEventListener('mousedown', this.onDown)
      window.addEventListener('mouseup', this.onUp)
      ov.addEventListener('mousemove', this.onMove)
      ov.addEventListener('wheel', this.onWheel, { passive: false })

      // ⛔️ deshabilitar menú nativo y cualquier menú propio
      ov.addEventListener('contextmenu', e => e.preventDefault())

      window.addEventListener('keydown', this.onKey)
    },
    onDown(e){
      const r = this.$refs.overlay.getBoundingClientRect()
      this.lastX = e.clientX - r.left
      this.lastY = e.clientY - r.top
      this.isDragging = true

      // botón derecho → ajustar WL/WW
      if (e.button === 2) {
        this.wlwwAdjusting = true
        this.wlwwStart = { x:this.lastX, y:this.lastY, wl:this.viewport.wl, ww:this.viewport.ww }
        return
      }

      // sólo con botón izquierdo arrancamos herramientas
      if (e.button !== 0) return

      const imgPt = this.toImg(this.lastX, this.lastY)
      if (this.tool === 'length') {
        this.drawing = { type:'length', x0:imgPt.x, y0:imgPt.y, x1:imgPt.x, y1:imgPt.y }
      } else if (this.tool === 'label') {
        this.drawing = { type:'label', x0:imgPt.x, y0:imgPt.y, x1:imgPt.x, y1:imgPt.y, text:'' }
      } else if (this.tool === 'ellipse') {
        this.drawing = { type:'ellipse', x0:imgPt.x, y0:imgPt.y, x1:imgPt.x, y1:imgPt.y }
      } else if (this.tool === 'freehand') {
        this.drawing = { type:'freehand', points:[{ x:imgPt.x, y:imgPt.y }] }
      }
    },
    async onUp() {
      if (!this.isDragging) return
      this.isDragging = false
      if (this.wlwwAdjusting) {
        this.wlwwAdjusting = false
        return
      }
      if (this.tool === 'rotate') { return }
      if (this.drawing) {
        if (this.drawing.type === 'length') {
          this.annotations.length.push({ ...this.drawing })
        } else if (this.drawing.type === 'label') {
          const { isConfirmed, value } = await this.$swal.fire({
            title: 'Etiqueta',
            input: 'text',
            inputLabel: 'Texto para la etiqueta',
            inputValue: '',
            showCancelButton: true,
            confirmButtonText: 'OK',
            cancelButtonText: 'Cancelar',
          })
          const txt = (isConfirmed && typeof value === 'string') ? value.trim() : ''

          if (txt) {
            this.drawing.text = txt
            this.annotations.arrows.push({ ...this.drawing })
          }
        } else if (this.drawing.type === 'ellipse') {
          this.annotations.ellipses.push({ ...this.drawing })
        } else if (this.drawing.type === 'freehand') {
          if (this.drawing.points.length > 1)
            this.annotations.freehands.push({ points: [...this.drawing.points] })
        }
        this.drawing = null
        this.draw()
      }
    },

    onMove(e) {
      if (!this.isDragging && !this.drawing) return

      const r = this.$refs.overlay.getBoundingClientRect()
      const x = e.clientX - r.left, y = e.clientY - r.top

      // --- NUEVO: si estamos ajustando WL/WW con botón derecho ---
      if (this.wlwwAdjusting) {
        const dx = x - this.wlwwStart.x
        const dy = y - this.wlwwStart.y

        // Horizontal → WW (contraste). Vertical → WL (brillo).
        const ww = this.wlwwStart.ww + dx * this.wlwwSensitivity.wwPerPx
        const wl = this.wlwwStart.wl + (-dy) * this.wlwwSensitivity.wlPerPx  // arriba = +WL

        this.viewport.ww = this.clampWW(Math.round(ww))
        this.viewport.wl = this.clampWL(Math.round(wl))
        this.draw()
        return
      }

      // --- existente: pan/dibujo ---
      if (this.tool === 'pan' && this.isDragging) {
        this.viewport.translateX += (x - this.lastX)
        this.viewport.translateY += (y - this.lastY)
        this.lastX = x; this.lastY = y; this.draw(); return
      }

      if (this.tool === 'rotate' && this.isDragging) {
        const dx = x - this.lastX
        this.viewport.rotateDeg = this.normalizeDeg(this.rotateStartDeg + dx * this.rotateDragSensitivity)
        this.draw()
        return
      }

      if (this.drawing) {
        const ip = this.toImg(x, y)
        if (this.drawing.type === 'length' || this.drawing.type === 'label' || this.drawing.type === 'ellipse') {
          this.drawing.x1 = ip.x; this.drawing.y1 = ip.y
        } else if (this.drawing.type === 'freehand') {
          const pts = this.drawing.points
          const prev = pts[pts.length - 1]
          const dx = ip.x - prev.x, dy = ip.y - prev.y
          if ((dx * dx + dy * dy) > 0.5 * 0.5) pts.push({ x: ip.x, y: ip.y })
        }
        this.draw()
      }
    },

    onWheel(e) {
      e.preventDefault()
      const factor = e.deltaY < 0 ? 1.1 : 0.9
      const r = this.$refs.overlay.getBoundingClientRect()
      this.zoomAt(factor, e.clientX - r.left, e.clientY - r.top)
    },
    onKey(e) {
      const k = e.key.toLowerCase()
      if (k === 'q') this.addRotation(-5)   // rotar -5°
      if (k === 'e') this.addRotation(+5)   // rotar +5°
      if (k === 'r') { this.resetView() }
      if (k === 's') { this.saveToServer() }
      if (k === 'd') { this.downloadPng() }
      if (k === 'm') { this.setTool('length') }
      if (k === 'l') { this.setTool('label') }
      if (k === 'o') { this.setTool('ellipse') }     // Óvalo
      if (k === 'f') { this.setTool('freehand') }
    },
  }
}
</script>

<style scoped>
:root {
  --bg: #0f1115;
  --panel: #12161d;
  --panel-2: #171c25;
  --text: #e5e7eb;
  --muted: #9aa3b2;
  --border: #202631;
  --accent: #66a6ff;
  --accent-2: #4cc9f0;
  --shadow: 0 10px 30px rgba(0, 0, 0, .35);
}

/* Estructura */
.layout {
  display: flex;
  flex-direction: column;
  height: 100vh;
  background: var(--bg);
  color: var(--text)
}

.topbar {
  position: sticky;
  top: 0;
  z-index: 5;
  display: flex;
  gap: 8px;
  align-items: center;
  background: var(--panel);
  border-bottom: 1px solid var(--border);
  padding: 8px 12px;
  box-shadow: var(--shadow);
}

.topbar .spacer {
  flex: 1
}

.wrap {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 320px;
  gap: 16px;
  padding: 16px;
  height: 100%;
  box-sizing: border-box;
  overflow: hidden;
}

/* Viewport */
#viewportWrap {
  position: relative;
  min-height: 480px;
  overflow: hidden;
  background: #0b0e14;
  border: 1px solid var(--border);
  border-radius: 12px;
  box-shadow: var(--shadow);
}

#viewportWrap canvas {
  position: absolute;
  inset: 0;
  image-rendering: pixelated
}

/* HUD */
.hud {
  position: absolute;
  left: 10px;
  bottom: 10px;
  font: 12px/1.1 system-ui, -apple-system, Segoe UI, Roboto, Arial;
  color: #d1d5db;
  background: rgba(0, 0, 0, .45);
  backdrop-filter: blur(2px);
  padding: 6px 10px;
  border: 1px solid rgba(255, 255, 255, .06);
  border-radius: 8px;
}

/* Panel derecho */
.right {
  display: flex;
  flex-direction: column;
  gap: 12px;
  overflow: auto;
  background: var(--panel);
  border: 1px solid var(--border);
  border-radius: 12px;
  padding: 14px;
  box-shadow: var(--shadow);
}

.metric {
  color: #cbd5e1;
  line-height: 1.35
}

.group .label {
  font-size: 12px;
  color: #b9c2d0;
  margin-bottom: 6px
}

.row.btns {
  display: flex;
  gap: 8px;
  margin-top: 8px
}

/* Controles */
.tool,
.btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 12px;
  border-radius: 10px;
  cursor: pointer;
  background: var(--panel-2);
  color: #e6edf7;
  border: 1px solid var(--border);
  transition: transform .06s ease, background .2s ease, border-color .2s ease;
}

.tool:hover,
.btn:hover {
  background: #1a2130;
  border-color: #2a3343
}

.tool:active,
.btn:active {
  transform: translateY(1px)
}

.tool.active {
  outline: 2px solid #24476b
}

.slider {
  width: 100%;
  height: 28px;
  background: transparent
}

.slider:focus {
  outline: none
}

.slider::-webkit-slider-runnable-track {
  height: 4px;
  background: #2b3240;
  border-radius: 999px;
  border: 1px solid #1e2430
}

.slider::-webkit-slider-thumb {
  -webkit-appearance: none;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: linear-gradient(180deg, var(--accent), var(--accent-2));
  border: 1px solid #0b0e14;
  margin-top: -6px;
  box-shadow: 0 2px 6px rgba(0, 0, 0, .4);
}

.slider::-moz-range-track {
  height: 4px;
  background: #2b3240;
  border-radius: 999px;
  border: 1px solid #1e2430
}

.slider::-moz-range-thumb {
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: linear-gradient(180deg, var(--accent), var(--accent-2));
  border: 1px solid #0b0e14;
  box-shadow: 0 2px 6px rgba(0, 0, 0, .4);
}

/* Menú contextual */
.ctx {
  position: absolute;
  z-index: 10;
  min-width: 190px;
  background: var(--panel);
  color: var(--text);
  border: 1px solid var(--border);
  border-radius: 10px;
  box-shadow: var(--shadow);
  padding: 6px;
}

.ctx button {
  display: block;
  width: 100%;
  text-align: left;
  background: transparent;
  border: 0;
  color: #d6d9df;
  padding: 8px 10px;
  border-radius: 8px;
  cursor: pointer;
}

.ctx button:hover {
  background: #1a2130
}

.ctx hr {
  border: none;
  border-top: 1px solid #2a3343;
  margin: 6px 0
}
</style>
